/**
 * ShaderProgram compiles, links, and manages WebGL shaders and uniforms
 */
export class ShaderProgram {
  constructor(gl, vertexSource, fragmentSource) {
    this.gl = gl;
    this.program = this.createProgram(vertexSource, fragmentSource);
    this.uniformLocations = new Map();
    this.attribLocations = new Map();
  }

  createShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Shader compile error (${type === gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT'}):\n${info}`);
    }
    return shader;
  }

  createProgram(vertexSource, fragmentSource) {
    const gl = this.gl;
    const vertShader = this.createShader(gl.VERTEX_SHADER, vertexSource);
    const fragShader = this.createShader(gl.FRAGMENT_SHADER, fragmentSource);

    const program = gl.createProgram();
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Shader link error:\n${info}`);
    }

    gl.deleteShader(vertShader);
    gl.deleteShader(fragShader);
    return program;
  }

  use() {
    this.gl.useProgram(this.program);
  }

  getUniformLocation(name) {
    if (!this.uniformLocations.has(name)) {
      const loc = this.gl.getUniformLocation(this.program, name);
      this.uniformLocations.set(name, loc);
    }
    return this.uniformLocations.get(name);
  }

  getAttribLocation(name) {
    if (!this.attribLocations.has(name)) {
      const loc = this.gl.getAttribLocation(this.program, name);
      this.attribLocations.set(name, loc);
    }
    return this.attribLocations.get(name);
  }

  set1i(name, val) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform1i(loc, val);
  }

  set1f(name, val) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform1f(loc, val);
  }

  set2f(name, x, y) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform2f(loc, x, y);
  }

  set3f(name, x, y, z) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform3f(loc, x, y, z);
  }

  set4f(name, x, y, z, w) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform4f(loc, x, y, z, w);
  }

  set4fv(name, data) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform4fv(loc, data);
  }

  set3fv(name, data) {
    const loc = this.getUniformLocation(name);
    if (loc) this.gl.uniform3fv(loc, data);
  }
}
